import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  LifeBuoy,
  Bug,
  Sparkles,
  HelpCircle,
  User,
  MoreHorizontal,
  CheckCircle2,
  Mail,
  Instagram,
  Twitter,
  Loader2,
  AlertCircle,
} from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: "bug", label: "Bug report", icon: Bug },
  { value: "feature", label: "Feature request", icon: Sparkles },
  { value: "question", label: "Question", icon: HelpCircle },
  { value: "account", label: "Account & data", icon: User },
  { value: "other", label: "Other", icon: MoreHorizontal },
];

const initialForm = {
  name: "",
  email: "",
  category: "question",
  subject: "",
  message: "",
};

export default function Support() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [ticket, setTicket] = useState(null);

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email";
    if (!form.subject.trim() || form.subject.trim().length < 3)
      e.subject = "Subject must be at least 3 characters";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Please share at least 10 characters so we can help";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    try {
      const { data } = await axios.post(`${API}/support`, form, {
        timeout: 15000,
      });
      setTicket(data);
      setStatus("success");
      setForm(initialForm);
      toast.success(`Ticket ${data.reference} created — we'll be in touch.`);
    } catch (err) {
      setStatus("error");
      const detail =
        err?.response?.data?.detail ||
        (err?.response?.status === 422
          ? "Please check your entries and try again."
          : "Something went wrong — please try again.");
      toast.error(detail);
    }
  };

  return (
    <div className="bg-black text-white min-h-screen">
      <Toaster
        theme="dark"
        toastOptions={{
          style: {
            background: "#0a0a0a",
            border: "1px solid #27272a",
            color: "#fff",
          },
        }}
      />
      <Nav />

      <main
        data-testid="support-page"
        className="pt-32 pb-20 max-w-5xl mx-auto px-6 md:px-10"
      >
        <Link
          to="/"
          data-testid="support-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-[#D4F34A] transition mb-10"
        >
          <ArrowLeft size={14} />
          Back to Rallie
        </Link>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Left column: heading + info */}
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-widest text-[#D4F34A] mb-3 flex items-center gap-2">
              <LifeBuoy size={14} />
              Support
            </div>
            <h1
              data-testid="support-heading"
              className="font-display uppercase tracking-tight text-5xl md:text-7xl leading-[0.9]"
            >
              We&apos;re here
              <br />
              to help.
            </h1>
            <p className="mt-6 text-neutral-300 text-base leading-relaxed">
              Bug, feature idea or a question about your account — drop us a
              note and we&apos;ll get back to you as fast as possible.
            </p>

            <div className="mt-10 border border-neutral-900 bg-neutral-950 p-6">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
                Response time
              </div>
              <div className="flex items-baseline gap-3 mb-6">
                <span className="font-display text-5xl text-[#D4F34A] leading-none">
                  &lt; 24h
                </span>
                <span className="text-xs text-neutral-500 uppercase tracking-widest">
                  Mon–Fri
                </span>
              </div>
              <div className="space-y-3 text-sm">
                <a
                  href="mailto:hello@rallie.app"
                  data-testid="support-email-link"
                  className="flex items-center gap-3 text-neutral-300 hover:text-[#D4F34A] transition"
                >
                  <Mail size={16} className="text-[#D4F34A]" />
                  hello@rallie.app
                </a>
                <div className="flex items-center gap-3 text-neutral-400">
                  <a
                    href="#"
                    aria-label="Instagram"
                    className="hover:text-[#D4F34A] transition"
                    data-testid="support-social-instagram"
                  >
                    <Instagram size={16} />
                  </a>
                  <a
                    href="#"
                    aria-label="Twitter/X"
                    className="hover:text-[#D4F34A] transition"
                    data-testid="support-social-twitter"
                  >
                    <Twitter size={16} />
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-6 text-xs text-neutral-500 leading-relaxed">
              For account deletion, use the{" "}
              <Link
                to="/delete-account"
                data-testid="support-delete-account-link"
                className="text-[#D4F34A] hover:underline"
              >
                Delete Account
              </Link>{" "}
              page.
            </div>
          </div>

          {/* Right column: form or success */}
          <div className="lg:col-span-7">
            {status === "success" && ticket ? (
              <div
                data-testid="support-success"
                className="border-2 border-[#D4F34A] bg-[#D4F34A]/[0.05] p-8 md:p-10"
              >
                <CheckCircle2
                  size={32}
                  className="text-[#D4F34A] mb-4"
                  strokeWidth={2}
                />
                <div className="text-xs uppercase tracking-widest text-[#D4F34A] mb-2">
                  Ticket created
                </div>
                <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight leading-tight">
                  We got your note.
                </h2>
                <p className="mt-4 text-sm md:text-base text-neutral-300 leading-relaxed">
                  Your reference number is{" "}
                  <span
                    data-testid="support-ticket-reference"
                    className="font-display text-white bg-black border border-neutral-800 px-2 py-1"
                  >
                    {ticket.reference}
                  </span>
                  . Keep it handy — we&apos;ll reply to your email within 24
                  hours on business days.
                </p>

                <button
                  type="button"
                  data-testid="support-new-ticket"
                  onClick={() => {
                    setStatus("idle");
                    setTicket(null);
                  }}
                  className="mt-8 inline-flex items-center gap-2 border border-neutral-700 text-white px-5 py-3 rounded-full font-display uppercase text-sm tracking-widest hover:border-[#D4F34A] hover:text-[#D4F34A] transition"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="support-form"
                noValidate
                className="border border-neutral-900 bg-neutral-950 p-6 md:p-10"
              >
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <Field
                    label="Your name"
                    name="name"
                    value={form.name}
                    onChange={(v) => setField("name", v)}
                    error={errors.name}
                    placeholder="Marco Rossi"
                    testId="support-name"
                  />
                  <Field
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={(v) => setField("email", v)}
                    error={errors.email}
                    placeholder="you@example.com"
                    testId="support-email"
                  />
                </div>

                <div className="mb-4">
                  <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
                    Category
                  </div>
                  <div
                    role="radiogroup"
                    aria-label="Category"
                    className="flex flex-wrap gap-2"
                  >
                    {categories.map((c) => {
                      const Icon = c.icon;
                      const active = form.category === c.value;
                      return (
                        <button
                          key={c.value}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          data-testid={`support-category-${c.value}`}
                          onClick={() => setField("category", c.value)}
                          className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded-full border transition ${
                            active
                              ? "bg-[#D4F34A] text-black border-[#D4F34A]"
                              : "border-neutral-800 text-neutral-300 hover:border-neutral-600"
                          }`}
                        >
                          <Icon size={14} />
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mb-4">
                  <Field
                    label="Subject"
                    name="subject"
                    value={form.subject}
                    onChange={(v) => setField("subject", v)}
                    error={errors.subject}
                    placeholder="Score not saving after 3rd set"
                    testId="support-subject"
                  />
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline justify-between mb-2">
                    <label
                      htmlFor="message"
                      className="text-[11px] uppercase tracking-widest text-neutral-500"
                    >
                      Message
                    </label>
                    <span className="text-[10px] text-neutral-600">
                      {form.message.length}/4000
                    </span>
                  </div>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="support-message"
                    rows={6}
                    maxLength={4000}
                    value={form.message}
                    onChange={(ev) => setField("message", ev.target.value)}
                    placeholder="Describe what happened, what you expected, and the device you're using…"
                    className={`w-full bg-black border ${
                      errors.message
                        ? "border-red-500/60"
                        : "border-neutral-800 focus:border-[#D4F34A]"
                    } px-4 py-3 text-sm text-white placeholder:text-neutral-600 outline-none transition resize-y`}
                  />
                  {errors.message && (
                    <FieldError message={errors.message} testId="support-message-error" />
                  )}
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="text-[11px] text-neutral-500 max-w-xs leading-relaxed">
                    By submitting, you agree we can contact you about your
                    request at the email above.
                  </p>
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    data-testid="support-submit"
                    className="inline-flex items-center gap-2 bg-[#D4F34A] text-black px-6 py-3 rounded-full font-display uppercase text-sm tracking-widest hover:bg-white transition active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "submitting" ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>Send ticket</>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

/* ---------------- Field helpers ---------------- */

function Field({ label, name, value, onChange, error, placeholder, type = "text", testId }) {
  return (
    <div>
      <label
        htmlFor={name}
        className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-2"
      >
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={(ev) => onChange(ev.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className={`w-full bg-black border ${
          error ? "border-red-500/60" : "border-neutral-800 focus:border-[#D4F34A]"
        } px-4 py-3 text-sm text-white placeholder:text-neutral-600 outline-none transition`}
      />
      {error && <FieldError message={error} testId={`${testId}-error`} />}
    </div>
  );
}

function FieldError({ message, testId }) {
  return (
    <div
      data-testid={testId}
      className="mt-2 flex items-center gap-1.5 text-[11px] text-red-400"
    >
      <AlertCircle size={12} />
      {message}
    </div>
  );
}
